import { Box, Grid, Typography } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import Flex from "../../../../../../components/Flex";
import StyledFormModal from "../../../../../../components/globalModal/StyledModal";
import StyledButtonV2 from "../../../../../../components/app/StyledButtonV2";
import { DisclosureHost } from "../../../../../../hooks/use-disclosure";
import {
  getErrorSnackbar,
  getSuccessSnackbar,
  getXtenantId,
} from "../../../../../../utils/utils";

import { useEffect, useState } from "react";
import Input from "../../../../../../components/Input";
import PasswordInput from "../../../../../../components/app/PasswordInput";
import { useSnackbar } from "../../../../../../hooks/useSnackbar";
import { useDisable2FA } from "../../../../../../queries/2fa/disable2FA";
import { useProviderProfile } from "../../../../../../queries/provider/setting/use-provider-profile";
import useCurrentUser, {
  setCurrentUser,
} from "@/hooks/local-storage/use-current-user";
interface EnableDisableAuthProps {
  disclosureHost: DisclosureHost;
}

const EnableDisableAuth = ({ disclosureHost }: EnableDisableAuthProps) => {
  const { mutateAsync: disable2FA } = useDisable2FA();
  const showSnackbar = useSnackbar();
  const [me] = useCurrentUser();
  const [enabled, setEnabled] = useState(!!me?.twoFAEnabled);

  const { data: ProviderProfileData } = useProviderProfile(
    getXtenantId() ?? undefined,
    enabled
  );
  useEffect(() => {
    if (enabled && ProviderProfileData) {
      setCurrentUser((ProviderProfileData as any)?.data);
    }
  }, [enabled, ProviderProfileData]);
  const initialValue = {
    email: "",
    password: "",
  };

  const validationSchema = Yup.object({
    email: Yup.string()
      .email("Invalid email format")
      .required("Email is required")
      .label("Email"),
    password: Yup.string().required("Password is required").label("Password"),
  });

  const formik = useFormik({
    initialValues: initialValue,
    validationSchema,
    onSubmit: handleSubmit,
    enableReinitialize: true,
  });

  async function handleSubmit(values?: any) {
    if (values) {
      disable2FA({ ...values, xTENANTID: getXtenantId() ?? undefined })
        .then((res) => {
          if (res.data.code === "TWO_FACTOR_AUTH_DISABLED") {
            setEnabled(false);
            showSnackbar(getSuccessSnackbar(res));
            closeAndResetForm();
          } else {
            showSnackbar({
              message: (res?.data?.message as any) || "Failed to disable 2FA",
              type: "error",
            });
          }
        })
        .catch((err) => {
          showSnackbar(getErrorSnackbar(err));
        });
    }
  }

  function closeAndResetForm() {
    formik.resetForm();
    disclosureHost.close();
  }

  return (
    <StyledFormModal
      open={disclosureHost.opened}
      onClose={closeAndResetForm}
      dialogTitle="Two-Factor Authentication Setup"
      dialogAction={
        <Flex justifyContent={"end"} gap={1}>
          <StyledButtonV2
            label="Cancel"
            variant="outlined"
            onClick={closeAndResetForm}
          />
          <StyledButtonV2
            label={enabled ? "Disable" : "Enable"}
            variant="contained"
            onClick={formik.submitForm}
            sx={{ width: "100px" }}
            disabled={!formik.isValid || formik.isSubmitting}
          />
        </Flex>
      }
      sx={{
        "& .MuiDialog-paper": {
          height: "auto",
          width: "400px",
        },
      }}
    >
      <Box sx={{ padding: "20px 0" }} pt={2}>
        <Typography
          sx={{
            fontWeight: "bold",
            color: "#004186",
            fontSize: "24px",
            marginBottom: "24px",
            textAlign: "center",
          }}
        >
          Two-Factor Authentication
        </Typography>

        <Typography
          sx={{
            color: "#666",
            fontSize: "14px",
            marginBottom: "32px",
            textAlign: "center",
          }}
        >
          Please enter your credentials to enable/disable 2FA
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Input
              textFieldProps={{
                name: "email",
                value: formik.values.email,
                onChange: formik.handleChange,
                onBlur: formik.handleBlur,
                placeholder: "Enter your email",
              }}
              required
              error={formik.touched?.email && formik.errors?.email}
            />
          </Grid>

          <Grid item xs={12}>
            <PasswordInput
              required
              error={formik.touched.password && (formik.errors.password as any)}
              // errorMessage={
              //   formik.touched.password ? formik.errors.password : undefined
              // }
              value={formik.values.password}
              onChange={(v) => {
                formik.setFieldValue("password", v);
              }}
            />
          </Grid>
        </Grid>

        <Box sx={{ marginTop: "24px" }}>
          <Typography
            sx={{
              color: "#666",
              fontSize: "12px",
              textAlign: "center",
              lineHeight: "1.4",
            }}
          >
            By proceeding, you agree to enable or disable two-factor
            authentication for your account.
          </Typography>
        </Box>
      </Box>
    </StyledFormModal>
  );
};

export default EnableDisableAuth;
